import { format } from 'date-fns'
import _ from 'lodash'
import { parseTimezoneISO } from './date'
import {
  FulfillmentType,
  FulfillmentWindow,
  OrderType,
  PREORDER_ORDER
} from 'shop/types'
import { ApolloError } from '@apollo/client'
import {
  OrderFulfillmentType,
  PartnerStore,
  Store,
  StoreStatus
} from 'shop/components/Landing'
import { History } from 'history'

export const getErrorMessage = (graphQLErrors: any): string => {
  return (graphQLErrors && graphQLErrors[0] && graphQLErrors[0].message) || ''
}

export const isNetworkErrorUnauthorized = (networkError: any): boolean => {
  return !!(
    networkError &&
    networkError.statusCode === 401 &&
    (networkError.bodyText === 'unauthorized' ||
      networkError.result === 'unauthorized')
  )
}

export const checkHandleUnauthorizedError = (
  error: ApolloError,
  domain: string,
  currentStore: Store | undefined,
  history: History
) => {
  const { networkError, graphQLErrors } = error

  const isUnauthorized =
    (networkError && isNetworkErrorUnauthorized(networkError)) ||
    (graphQLErrors &&
      ['unauthorized', 'auth_missing'].includes(getErrorMessage(graphQLErrors)))

  if (isUnauthorized) {
    // If unauth'd clear cart related credentials & return to shop
    localStorage.removeItem(domain)
    localStorage.removeItem(`${domain}-csrf`)
    returnToShop(currentStore, history)
    return
  }
}

export const returnToShop = (
  store: Store | PartnerStore | undefined | null,
  history: History
) => {
  !!store ? history.push(`/store/${store.slug}`) : history.push('/')
}

/** The 'keysHaveValues' function checks if any of the specified keys in the object have non-empty values.
 * returns true if any of the specified keys have non-empty values, false otherwise. */
export const keysHaveValues = (
  keys: string[],
  obj: Record<string, any>
): boolean => {
  return _.some(keys, (key) => !_.isEmpty(obj[key]))
}

export const emailRegex =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/

/** Returns fulfillment time range. Example output: "14:00 - 14:30" */
export const formatFulfillmentWindow = (
  window: FulfillmentWindow,
  timeDateFormat: string = 'HH:mm'
): string =>
  `${format(
    parseTimezoneISO(window.from),
    timeDateFormat
  )} - ${format(parseTimezoneISO(window.to), timeDateFormat)}`

export const getImageURL = (
  domain: string,
  assetHost: string,
  variantID: string
) => `https://${domain}.${assetHost}/assets/variant/${variantID}`

/** Move a list of given keys to a new property within the same object  */
export const mapKeysToNewProperty = (
  obj: Object,
  keysToExtract: string[],
  newKey: string
) => {
  // Empty object to hold the extracted keys.
  const extractedKeys = {}
  // Mutable object
  const tempObj = { ...obj }

  // Loops through the keys to extract and delete them from the original object.
  keysToExtract.forEach((key) => {
    if (key in tempObj) {
      extractedKeys[key] = tempObj[key]
      delete tempObj[key]
    }
  })

  // Assign the object of extracted keys to a new key within the original object.
  tempObj[newKey] = extractedKeys

  return tempObj
}

export const findDefaultFulfillment = (
  orderFulfillmentTypes: OrderFulfillmentType[] | undefined,
  fulfillmentType?: FulfillmentType
) => {
  if (!orderFulfillmentTypes) return null

  // If fulfillment type is specified, return first orderFulfillmentType that matches the type and is accepting orders
  if (fulfillmentType) {
    return orderFulfillmentTypes.find(
      (orderFulfillmentType) =>
        orderFulfillmentType.fulfillmentType === fulfillmentType &&
        orderFulfillmentType.status === 'ACCEPTING_ORDERS'
    )
  }
  // Otherwise return first orderFulfillmentType that is accepting orders
  return orderFulfillmentTypes.find(
    (orderFulfillmentType) => orderFulfillmentType.status === 'ACCEPTING_ORDERS'
  )
}

/** Checks if there is store that is currently open or accepting preorders */
export const isAnyStoreAcceptingOrders = (
  stores: PartnerStore[] | undefined
) => {
  if (!stores?.length) return false
  return stores.some(
    (store) =>
      !!store.orderFulfillmentTypes?.length &&
      (store.status === StoreStatus.OPEN ||
        store.status === StoreStatus.ACCEPTING_PREORDER)
  )
}

/** Checks if a store offers any fulfillment options for a given fulfillment type (regardless of availability) */
export const hasFulfillmentType = (
  store: PartnerStore | undefined,
  fulfillmentType: FulfillmentType
) => {
  if (!store?.orderFulfillmentTypes?.length || !fulfillmentType) return false
  return store.orderFulfillmentTypes.some(
    ({ fulfillmentType: fType }) => fType === fulfillmentType
  )
}

/** Checks that provided a store is accepting preorder only the passed in orderType is for preorder */
const canPreorder = (storeStatus: StoreStatus, orderType: OrderType) =>
  storeStatus === StoreStatus.ACCEPTING_PREORDER && orderType === PREORDER_ORDER

/** Checks if a store is available for a given fulfillment type */
export const isFulfillmentTypeAvailable = (
  store: PartnerStore | undefined,
  fulfillmentType: FulfillmentType
) => {
  if (!store?.orderFulfillmentTypes?.length || !fulfillmentType) return false

  const storeIsOpen = store.status === StoreStatus.OPEN
  // Check if the store has this fulfillment type & is either open or accepts preorder for this fulfillment type
  return store.orderFulfillmentTypes.some(
    (oft) =>
      oft.fulfillmentType === fulfillmentType &&
      oft.status === 'ACCEPTING_ORDERS' &&
      (storeIsOpen || canPreorder(store.status, oft.orderType))
  )
}

/** Checks if a store is accepting orders, either for a specific fulfillment type or for any fulfillment type */
export const canAcceptOrders = (
  store: PartnerStore | null,
  fulfillmentType?: FulfillmentType
) => {
  if (!store) return false

  // If specific fulfillment type is provided, check that one
  if (fulfillmentType) {
    return isFulfillmentTypeAvailable(store, fulfillmentType)
  }
  const storeIsOpen = store.status === StoreStatus.OPEN
  // Otherwise check if any fulfillment type is available
  return store.orderFulfillmentTypes.some(
    (oft) =>
      oft.status === 'ACCEPTING_ORDERS' &&
      (storeIsOpen || canPreorder(store.status, oft.orderType))
  )
}

/** Capitalizes snake case strings */
export const capitalizeWords = (str: string) => {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}
